import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { IPaginationParams } from '../../services/types';
import { AppContext } from '../../context/AppContextProvider';
import TableComponent, { IColumnProps } from '../../components/TableComponent';
import ButtonActionsComponent from '../../components/ButtonActionsComponent';
import ButtonComponent from '../../components/ButtonComponent';
import { Add } from '@mui/icons-material';
import ConfirmDeleteModalComponent from '../../components/ConfirmDeleteModalComponent';
import {
  ISchoolBoardFilterParams,
  ISchoolBoardProps,
  ISchoolBoardSimpleProps,
} from '../../services/SchoolBoard/types';
import {
  useGetAllSchoolBoardsByLicenseAsync,
  useGetAllSchoolBoardsByLicenseCountAsync,
} from '../../services/SchoolBoard/hooks/useQuery';
import {
  useDeleteSchoolBoardAsync,
  useGetSchoolBoardByIdAsync,
} from '../../services/SchoolBoard/hooks/useMutation';
import ImageModalComponent from '../../components/ImageModalComponent';
import ImageSimpleColumnComponent from '../../components/ImageSimpleColumnComponent';
import MarkChatReadIcon from '@mui/icons-material/MarkChatRead';
import dayjs from 'dayjs';
import SchoolBoardEditModal from './_components/SchoolBoardEditModal';
import SchoolBoardReadModal from './_components/SchoolBoardReadModal'; 

const SchoolBoardPage = () => {
  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);

  const [filter, setFilter] = useState<ISchoolBoardFilterParams>({
    search: '',
  });
  const [pagination, setPagination] = useState<IPaginationParams>({
    pageNumber: 1,
    pageSize: 10,
  });

  const [isShowImageModal, setIsShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [isVisibleEditModal, setIsVisibleEditModal] = useState(false);
  const [isVisibleDeleteModal, setIsVisibleDeleteModal] = useState(false);
  
  // Estados para o Modal de Leitura
  const [isVisibleReadModal, setIsVisibleReadModal] = useState(false);
  const [selectedToViewRead, setSelectedToViewRead] = useState<number>();

  const [selectedToEdit, setSelectedToEdit] = useState<ISchoolBoardProps>();
  const [selectedToDelete, setSelectedToDelete] =
    useState<ISchoolBoardSimpleProps>();

  const {
    data: schoolBoards,
    refetch: refetchList,
    isLoading: isLoadingList,
  } = useGetAllSchoolBoardsByLicenseAsync(pagination, filter);

  const {
    data: totalCount,
    refetch: refetchCount,
    isLoading: isLoadingCount,
  } = useGetAllSchoolBoardsByLicenseCountAsync(pagination, filter);

  const { mutateAsync: mutateGetById } = useGetSchoolBoardByIdAsync();
  const { mutateAsync: mutateDelete } = useDeleteSchoolBoardAsync();

  const refetchAll = useCallback(async () => {
    await Promise.all([refetchList(), refetchCount()]);
  }, [refetchList, refetchCount]);

  const handleRead = (id: number) => {
    setSelectedToViewRead(id);
    setIsVisibleReadModal(true);
  };

  const handleEdit = async (id: number) => {
    setIsShowLoadingComponent(true);
    try {
      const data = await mutateGetById(id);
      setSelectedToEdit(data);
      setIsVisibleEditModal(true);
    } catch (error: any) {
      showSnackbar(error.message || 'Erro ao carregar dados', 'error');
    } finally {
      setIsShowLoadingComponent(false);
    }
  };

  const handleDeleteClick = (id: number) => {
    const item = schoolBoards?.find((i) => i.id === id);
    setSelectedToDelete(item);
    setIsVisibleDeleteModal(true);
  };

  const handleConfirmDeletion = async () => {
    if (!selectedToDelete?.id) return;

    setIsShowLoadingComponent(true);
    try {
      await mutateDelete(selectedToDelete.id);
      showSnackbar('Recado excluído com sucesso!', 'success');
      setIsVisibleDeleteModal(false);
      refetchAll();
    } catch (error: any) {
      showSnackbar(error.message, 'error');
    } finally {
      setIsShowLoadingComponent(false);
    }
  };

  const handleImageClick = (url: string) => {
    setSelectedImage(url);
    setIsShowImageModal(true);
  };

  const columns: IColumnProps[] = useMemo(
    () => [
      {
        id: 'name',
        label: 'Nome',
        width: 'auto',
      },
      {
        id: 'initialDate',
        label: 'Início em',
        width: 'auto',
        render: (val) => (
          <>{val ? dayjs(val).utc().format('DD/MM/YYYY') : '-'}</>
        ),
      },
      {
        id: 'validUntil',
        label: 'Validade',
        width: 'auto',
        render: (val) => (
          <>{val ? dayjs(val).utc().format('DD/MM/YYYY') : '-'}</>
        ),
      },
      {
        id: 'image',
        label: 'Imagem',
        width: 'auto',
        align: 'center',
        render: (url) => (
          <ImageSimpleColumnComponent
            url={String(url)}
            onClick={() => handleImageClick(String(url))}
          />
        ),
      },
      {
        id: 'id',
        label: 'Ações',
        align: 'center',
        width: '120px', 
        render: (id) => (
          <ButtonActionsComponent
            isWithDefaultOptionsList
            actionOptions={[
              {
                label: 'Confirmações de leitura',
                icon: <MarkChatReadIcon fontSize="small" />,
                onClick: () => handleRead(Number(id)),
              },
            ]}
            handleEdit={() => handleEdit(Number(id))}
            handleDelete={() => handleDeleteClick(Number(id))}
          />
        ),
      },
    ],
    [schoolBoards]
  );

  return (
    <>
      <TableComponent
        columns={columns}
        rows={schoolBoards ?? []}
        sortByName="name"
        isLoading={isLoadingList || isLoadingCount}
        title="Mensageria"
        totalCount={totalCount ?? 0}
        onSubmitMainFilter={refetchAll}
        onChangeMainFilter={(val) =>
          setFilter((prev) => ({ ...prev, search: val as string }))
        }
        searchValue={filter.search}
        onChangePageOrRowsPerPage={setPagination}
        additionalTitleComponent={
          <ButtonComponent
            onClick={() => setIsVisibleEditModal(true)}
            startIcon={<Add />}
          >
            Novo Recado
          </ButtonComponent>
        }
      />

      <ConfirmDeleteModalComponent
        handleCancelDeletion={() => setIsVisibleDeleteModal(false)}
        handleConfirmDeletion={handleConfirmDeletion}
        isVisibleDeleteDialog={isVisibleDeleteModal}
        message={`Deseja excluir o recado "${selectedToDelete?.name}"?`}
        setIsVisibleDeleteDialog={setIsVisibleDeleteModal}
        title="Deletar Recado"
      />

      <ImageModalComponent
        isOpen={isShowImageModal}
        handleClose={() => setIsShowImageModal(false)}
        imageUrl={selectedImage}
        title="Visualização da Imagem"
        maxWidth="100%"
        maxHeight="100%"
      />

      {isVisibleEditModal && (
        <SchoolBoardEditModal
          isOpen={isVisibleEditModal}
          handleClose={() => {
            setIsVisibleEditModal(false);
            setSelectedToEdit(undefined);
          }}
          handleSaveForm={refetchAll}
          schoolBoardToEdit={selectedToEdit}
        />
      )}

      {isVisibleReadModal && (
        <SchoolBoardReadModal 
          isOpen={isVisibleReadModal} 
          handleClose={() => {
            setIsVisibleReadModal(false);
            setSelectedToViewRead(undefined);
          }} 
          schoolBoardId={selectedToViewRead} 
        />
      )}
    </>
  );
};

export default SchoolBoardPage;